
import React, { PureComponent } from 'react';

import { translate } from '../../../base/i18n';
import { Icon, IconInfo, IconVolume, IconVolumeOff } from '../../../base/icons';
import { isLocalParticipantModerator } from '../../../base/participants';
import { LoadingIndicator, Switch } from '../../../base/react';
import { connect } from '../../../base/redux';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { isInBreakoutRoom } from '../../../breakout-rooms/functions';
import { showLobbySoundScopeDialog, toggleLobbyMode } from '../../actions';
import { checkLobbyRateLimit } from '../../functions';

type Props = {

    /**
     * True if lobby is currently enabled in the conference.
     */
    _lobbyEnabled: boolean,

    /**
     * True if lobby toggle action is currently loading.
     */
    _lobbyLoading: boolean,

    /**
     * True if the section should be visible.
     */
    _visible: boolean,

    /**
     * The Redux Dispatch function.
     */
    dispatch: Function,

    /**
     * Function to be used to translate i18n labels.
     */
    t: Function
};

type State = {

    /**
     * True if the lobby switch is toggled on.
     */
    lobbyEnabled: boolean
}

/**
 * Implements a security feature section to control lobby mode.
 */
class LobbySection extends PureComponent<Props, State> {
    /**
     * Instantiates a new component.
     *
     * @inheritdoc
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            lobbyEnabled: props._lobbyEnabled
        };

        this._onToggleLobby = this._onToggleLobby.bind(this);
        this._onToggleLobbySound = this._onToggleLobbySound.bind(this);
    }

    /**
     * Implements React's {@link Component#getDerivedStateFromProps()}.
     *
     * @inheritdoc
     */
    static getDerivedStateFromProps(props: Props, state: Object) {
        if (props._lobbyEnabled !== state.lobbyEnabled) {

            return {
                lobbyEnabled: props._lobbyEnabled
            };
        }

        return null;
    }

    /**
     * Implements {@code PureComponent#render}.
     *
     * @inheritdoc
     */
    render() {
        const { _visible, _lobbyLoading, disabledLobbySound, hide, t } = this.props;


        if (!_visible) {
            return null;
        }

        const lobbySwitchContainerStyle = {
            justifyContent: this.state.lobbyEnabled ? 'flex-end' : 'flex-start'
        };

        const renderSwitch = () => (
            <>
                {_lobbyLoading && (
                    <div
                        className = 'meethour-lobby-switch-container'
                        style = { lobbySwitchContainerStyle }>
                        <LoadingIndicator size = 'small' />
                    </div>
                )}
                <div className = { _lobbyLoading ? 'lobby-loader' : '' }>
                    <Switch
                        id = 'lobby-section-switch'
                        onValueChange = { this._onToggleLobby }
                        value = { this.state.lobbyEnabled } />
                </div>
            </>

        );

        return (

            <div id = 'lobby-section'>
                <div className = 'control-row'>
                    {!hide && <div className = { `lobby-section-child ${hide ? 'lobby-section-child-dialog' : 'lobby-section-child-pane'}` }>
                        <label htmlFor = 'lobby-section-switch'>
                            { t('lobby.dialogTitle') }
                        </label>
                        <Tooltip
                            content = { t('lobby.enableDialogText') }
                            position = 'bottom-left'>
                            <Icon
                                size = { 20 }
                                src = { IconInfo } />
                        </Tooltip>
                    </div>}
                    <div className = { `lobby-section-child ${hide ? 'lobby-section-child-dialog' : 'lobby-section-child-pane'}` }>
                        {hide ? <Tooltip
                            content = { t('lobby.enableDialogText') }
                            position = 'bottom-right'>
                            <div className = 'lobby-section-info'>
                                <Icon
                                    size = { 20 }
                                    src = { IconInfo } />
                                { renderSwitch() }
                            </div></Tooltip>
                            : renderSwitch()}
                        <Tooltip
                            content = { disabledLobbySound ? t('lobby.unMuteLobbySound') : t('lobby.muteLobbySound') }
                            position = 'bottom-right'>
                            <Icon
                                onClick = { this._onToggleLobbySound }
                                size = { 20 }
                                src = { disabledLobbySound ? IconVolumeOff : IconVolume } />
                        </Tooltip>
                    </div>
                </div>
            </div>
        );
    }


    _onToggleLobby: () => void;

    /**
     * Callback to be invoked when the user toggles the lobby feature on or off.
     *
     * @returns {void}
     */
    _onToggleLobby() {
        if (this.props._lobbyLoading || checkLobbyRateLimit(this.props.dispatch)) {
            return;
        }

        const newValue = !this.state.lobbyEnabled;

        this.setState({
            lobbyEnabled: newValue
        });

        this.props.dispatch(toggleLobbyMode(newValue));
    }

    /**
     * Callback to be invoked when the user toggles the lobby sound on or off.
     *
     * @returns {void}
     */
    _onToggleLobbySound() {
        this.props.dispatch(showLobbySoundScopeDialog());
    }
}

/**
 * Maps part of the Redux state to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {Props}
 */
function mapStateToProps(state: Object): $Shape<Props> {
    const { conference } = state['features/base/conference'];
    const { hideLobbyButton } = state['features/base/config'];
    const { lobbyEnabled, lobbyLoading, disabledLobbySound } = state['features/lobby'];

    return {
        _lobbyEnabled: lobbyEnabled,
        _lobbyLoading: lobbyLoading,
        disabledLobbySound,
        _visible: conference && conference.isLobbySupported() && isLocalParticipantModerator(state)
            && !hideLobbyButton && !isInBreakoutRoom(state)
    };
}

/**
 * Maps part of the Redux state to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {Props}
 */
function mapDispatchToProps(dispatch) {
    return {
        dispatch
    };
}

export default translate(connect(mapStateToProps, mapDispatchToProps)(LobbySection));
